import { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelItem } from '../items';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SidebarTabTarget } from '../SidebarTabContainer';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { useGetBookmarkedViewModelIdsContextValues } from '@group/hooks/useGetBookmarkedViewModelIdsContextValues';
import type { ViewModelId, WorkspaceId } from 'balus-common';
import { Workspace } from '@workspace/domain/workspace';

type Props = {
  target: SidebarTabTarget;
  scrolledViewModelId: ViewModelId;
};

export const SidebarBookmarks: React.FC<Props> = ({ target }: Props) => {
  const { t } = useTranslation();
  const { bookmarkedViewModelIds } = useGetBookmarkedViewModelIdsContextValues();
  const [bookmarkedViewModels, setBookmarkedViewModels] = useState<{ [key: ViewModelId]: ViewModelEntity }>({});
  const [workspaces, setWorkspaces] = useState<{ [key: WorkspaceId]: Workspace }>({});
  const [hasBeenBookmarkTabShown, setHasBeenBookmarkTabShown] = useState(false);

  // 一度でもBookmarkタブが選択されたかを記録する
  useEffect(() => {
    if (target === 'Bookmark') {
      setHasBeenBookmarkTabShown(true);
    }
  }, [target]);

  useEffect(() => {
    // 一度でもBookmarkタブが開かれた段階でお気に入りされたビューモデルを監視する
    if (!hasBeenBookmarkTabShown) return;

    const viewModelRepos = Object.fromEntries(
      Array.from(bookmarkedViewModelIds).map((id) => [
        id,
        new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(id)),
      ])
    );

    Object.entries(viewModelRepos).forEach(([id, repo]) => {
      repo.addListener((viewModel) => {
        if (viewModel && !viewModel.isTrashed()) {
          setBookmarkedViewModels((prev) => ({ ...prev, [viewModel.id]: viewModel }));
        } else {
          setBookmarkedViewModels((prev) => {
            const { [id]: _, ...rest } = prev;
            return rest;
          });
        }
      });
    });

    return () => {
      Object.values(viewModelRepos).forEach((repo) => {
        repo.removeListener();
      });
    };
  }, [bookmarkedViewModelIds, hasBeenBookmarkTabShown]);

  useEffect(() => {
    if (!hasBeenBookmarkTabShown) return;

    const workspaceRepos = Object.fromEntries(
      Array.from(new Set(Object.values(bookmarkedViewModels).map((vm) => vm.workspaceId))).map((id) => [
        id,
        new ObjectRepository(Workspace, RTDBPath.Workspace.workspaceEntityPath(id)),
      ])
    );

    Object.entries(workspaceRepos).forEach(([id, repo]) => {
      repo.addListener((workspace) => {
        if (workspace) {
          setWorkspaces((prev) => ({ ...prev, [id]: workspace }));
        }
      });
    });

    return () => {
      Object.values(workspaceRepos).forEach((repo) => {
        repo.removeListener();
      });
    };
  }, [bookmarkedViewModels, hasBeenBookmarkTabShown]);

  const sortedBookmarkedViewModels = useMemo(() => {
    return Object.values(bookmarkedViewModels)
      .filter((vm) => !vm.isTrashed() && bookmarkedViewModelIds.has(vm.id))
      .sort(ViewModelEntity.compare);
  }, [bookmarkedViewModels, bookmarkedViewModelIds]);

  if (target !== 'Bookmark') return null;

  if (sortedBookmarkedViewModels.length <= 0) {
    return <div className="mx-2 mt-4">{t('sidebar.bookmarks.empty')}</div>;
  }

  return (
    <>
      {sortedBookmarkedViewModels.map((vm) => {
        const workspace = workspaces[vm.workspaceId];
        if (!workspace) return null;
        return <ViewModelItem key={vm.id} viewModel={vm} workspace={workspace} />;
      })}
    </>
  );
};
